import { debounce } from 'lodash';
import { useMemo, useState, ChangeEvent, useCallback, useEffect } from 'react';

import { alpha, Card, Tab, Tabs, Typography } from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';
import { useSearchParams } from 'react-router-dom';

import { useTranslate } from 'src/locales';
import { PageOptions } from 'src/services/api.types';
import { useGetMeetingsQuery } from 'src/services/meetings/meetings.service';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';
import { AccountRole } from 'src/services/organisation/organisation.enum';
import MeetingEditorModal from 'src/sections/meetings/modal/meeting-editor-modal';
import MeetingTable from '../table/meeting-table';

const DEFAULT_PAGE: PageOptions = {
  page: 1,
  per_page: 10,
};

export default function NotetakerListView() {
  const { t } = useTranslate();

  const createMeetingControl = useBoolean();

  const authContext = useAuthContext();

  const [searchParams, setSearchParams] = useSearchParams();

  const [pageOptions, setPageOptions] = useState<PageOptions>(DEFAULT_PAGE);
  const [filterQuery, setFilterQuery] = useState<string | undefined>();

  const { data: meetings, isLoading: isMeetingsLoading } = useGetMeetingsQuery(
    {
      page: pageOptions.page,
      per_page: pageOptions.per_page,
      search_query: filterQuery,
    },
    {
      refetchOnMountOrArgChange: true
    }
  );

  const onChangePage = useCallback((_event: any, pageNumber: number) => {
    setPageOptions((prev) => ({
      ...prev,
      page: pageNumber + 1,
    }));
  }, []);

  const onChangeRowsPerPage = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setPageOptions((prev) => ({
        ...prev,
        per_page: +event.target.value,
      }));
    },
    []
  );

  const handleFilters = useMemo(
    () => debounce((value: string) => {
      setFilterQuery(value);
      setPageOptions((prev) => ({
        ...prev,
        page: 1, // Reset to first page on filter change
      }));
    }, 500),
    []
  );

  const handleAddMeeting = useCallback(() => {
    createMeetingControl.onTrue();
  }, [createMeetingControl]);

  const onMeetingCreated = useCallback(() => {
    createMeetingControl.onFalse();
  }, [createMeetingControl]);

  return (
    <Card>

      <MeetingTable
        meetings={meetings}
        pageOptions={pageOptions}
        isLoading={isMeetingsLoading}
        onFilters={handleFilters}
        onAddMeeting={handleAddMeeting}
        onChangePage={onChangePage}
        onChangeRowsPerPage={onChangeRowsPerPage}
      />


      {
        createMeetingControl.value && (
          <MeetingEditorModal open={createMeetingControl.value} onClose={onMeetingCreated} />
        )
      }

    </Card>
  );
}